<template>
  <div class="preview-container">
    <div class="container py-4">
      <!-- Step Header -->
      <div class="step-header mb-4">
        <h2 class="text-center">Step 7: Preview & Create Dashboard</h2>
        <p class="text-center text-muted">
          Review your configuration and create your custom MSDAT dashboard
        </p>
      </div>

      <!-- Configuration Complete Check -->
      <b-alert v-if="!isConfigComplete" show variant="warning" class="mb-4">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        <strong>Configuration Incomplete</strong>
        <p class="mb-2">Please complete all required steps before creating your dashboard:</p>
        <ul class="mb-0">
          <li v-if="!config">Dashboard configuration not set</li>
          <li v-if="!hasData">No data uploaded or transformed</li>
          <li v-if="!columnMapping">Column mapping not configured</li>
        </ul>
      </b-alert>

      <div class="row">
        <!-- Left Column: Configuration Summary -->
        <div class="col-lg-6">
          <!-- Basic Information Card -->
          <b-card class="summary-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-info-circle mr-2 text-primary"></i>
                <strong>Dashboard Information</strong>
                <b-badge variant="success" class="ml-auto" v-if="config">
                  <i class="fas fa-check mr-1"></i>
                  Configured
                </b-badge>
              </div>
            </template>

            <div v-if="config">
              <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value">{{ config.name || 'Not specified' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Description:</span>
                <span class="info-value">{{ config.description || 'Not specified' }}</span>
              </div>
              <div class="info-row" v-if="config.indicators">
                <span class="info-label">Indicators:</span>
                <span class="info-value">{{ config.indicators.length }} selected</span>
              </div>
              <div class="info-row" v-if="config.dataSources">
                <span class="info-label">Data Sources:</span>
                <span class="info-value">{{ config.dataSources.length }} selected</span>
              </div>
            </div>
            <div v-else class="text-muted">
              <i class="fas fa-times-circle mr-1"></i>
              Not configured
            </div>
          </b-card>

          <!-- Data Summary Card -->
          <b-card class="summary-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-database mr-2 text-info"></i>
                <strong>Data Summary</strong>
                <b-badge :variant="hasData ? 'success' : 'secondary'" class="ml-auto">
                  <i :class="hasData ? 'fas fa-check' : 'fas fa-times'" class="mr-1"></i>
                  {{ hasData ? 'Ready' : 'Not Ready' }}
                </b-badge>
              </div>
            </template>

            <div v-if="dataSummary">
              <div class="data-stats">
                <div class="stat-item">
                  <span class="stat-value">{{ dataSummary.totalRows }}</span>
                  <span class="stat-label">Total Records</span>
                </div>
                <div class="stat-item">
                  <span class="stat-value">{{ dataSummary.indicatorCount }}</span>
                  <span class="stat-label">Indicators</span>
                </div>
                <div class="stat-item">
                  <span class="stat-value">{{ dataSummary.locationCount }}</span>
                  <span class="stat-label">Locations</span>
                </div>
                <div class="stat-item">
                  <span class="stat-value">{{ dataSummary.periodCount }}</span>
                  <span class="stat-label">Periods</span>
                </div>
              </div>

              <hr />

              <h6 class="text-muted small text-uppercase mb-2">Indicators</h6>
              <div class="tag-list mb-3">
                <b-badge
                  v-for="ind in dataSummary.indicators.slice(0, 5)"
                  :key="ind"
                  variant="light"
                  class="mr-1 mb-1"
                >
                  {{ ind }}
                </b-badge>
                <b-badge v-if="dataSummary.indicators.length > 5" variant="secondary">
                  +{{ dataSummary.indicators.length - 5 }} more
                </b-badge>
              </div>

              <h6 class="text-muted small text-uppercase mb-2">Locations</h6>
              <div class="tag-list">
                <b-badge
                  v-for="loc in dataSummary.locations.slice(0, 5)"
                  :key="loc"
                  variant="light"
                  class="mr-1 mb-1"
                >
                  {{ loc }}
                </b-badge>
                <b-badge v-if="dataSummary.locations.length > 5" variant="secondary">
                  +{{ dataSummary.locations.length - 5 }} more
                </b-badge>
              </div>
            </div>
            <div v-else class="text-muted">
              <i class="fas fa-times-circle mr-1"></i>
              No data uploaded
            </div>
          </b-card>

          <!-- Appearance Card -->
          <b-card class="summary-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-palette mr-2 text-warning"></i>
                <strong>Appearance</strong>
              </div>
            </template>

            <div v-if="appearance && appearance.colors">
              <div class="color-preview">
                <div
                  class="color-swatch"
                  :style="{ backgroundColor: appearance.colors.primary }"
                  title="Primary"
                ></div>
                <div
                  class="color-swatch"
                  :style="{ backgroundColor: appearance.colors.secondary }"
                  title="Secondary"
                ></div>
                <div
                  class="color-swatch"
                  :style="{ backgroundColor: appearance.colors.accent }"
                  title="Accent"
                ></div>
                <div
                  class="color-swatch"
                  :style="{ backgroundColor: appearance.colors.background }"
                  title="Background"
                ></div>
              </div>
              <p class="text-muted small mt-2 mb-0">
                Theme: {{ appearance.theme || 'Light' }}
              </p>
            </div>
            <div v-else class="text-muted">
              <i class="fas fa-info-circle mr-1"></i>
              Using default theme
            </div>
          </b-card>
        </div>

        <!-- Right Column: Preview & Actions -->
        <div class="col-lg-6">
          <!-- Live Preview Card -->
          <b-card class="preview-card mb-4">
            <template #header>
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <i class="fas fa-eye mr-2 text-success"></i>
                  <strong>Dashboard Preview</strong>
                </div>
                <b-button
                  v-if="hasData"
                  variant="outline-primary"
                  size="sm"
                  @click="openFullPreview"
                >
                  <i class="fas fa-expand mr-1"></i>
                  Full Preview
                </b-button>
              </div>
            </template>

            <div v-if="hasData" class="preview-mini">
              <div class="preview-header" :style="previewHeaderStyle">
                <h6 class="mb-0 text-white">{{ config?.name || 'My Dashboard' }}</h6>
              </div>
              <div class="preview-body">
                <div class="preview-stats">
                  <div class="mini-stat">
                    <span class="mini-value">{{ dataSummary?.indicatorCount || 0 }}</span>
                    <span class="mini-label">Indicators</span>
                  </div>
                  <div class="mini-stat">
                    <span class="mini-value">{{ dataSummary?.locationCount || 0 }}</span>
                    <span class="mini-label">Locations</span>
                  </div>
                  <div class="mini-stat">
                    <span class="mini-value">{{ dataSummary?.totalRows || 0 }}</span>
                    <span class="mini-label">Data Points</span>
                  </div>
                </div>
                <div class="preview-chart-placeholder">
                  <i class="fas fa-chart-bar fa-3x text-muted"></i>
                  <p class="text-muted small mt-2">Charts will render with your data</p>
                </div>
              </div>
            </div>
            <div v-else class="no-preview text-center py-5">
              <i class="fas fa-eye-slash fa-3x text-muted mb-3"></i>
              <p class="text-muted">Complete configuration to see preview</p>
            </div>
          </b-card>

          <!-- Actions Card -->
          <b-card class="actions-card">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-rocket mr-2 text-danger"></i>
                <strong>Create Your Dashboard</strong>
              </div>
            </template>

            <div class="action-buttons">
              <b-button
                variant="primary"
                size="lg"
                block
                :disabled="!isConfigComplete || isCreatingDashboard"
                @click="createDashboard"
              >
                <b-spinner v-if="isCreatingDashboard" small class="mr-2"></b-spinner>
                <i v-else :class="activeInstanceId ? 'fas fa-save mr-2' : 'fas fa-plus-circle mr-2'"></i>
                {{ isCreatingDashboard
                  ? (activeInstanceId ? 'Updating Dashboard Instance...' : 'Creating Dashboard Instance...')
                  : (activeInstanceId ? 'Update Dashboard Instance' : 'Create Dashboard Instance') }}
              </b-button>

              <b-button
                variant="outline-primary"
                block
                :disabled="!config"
                @click="downloadConfig"
              >
                <i class="fas fa-download mr-2"></i>
                Download Configuration (JSON)
              </b-button>

              <b-button
                variant="outline-secondary"
                block
                @click="resetAndStartOver"
              >
                <i class="fas fa-redo mr-2"></i>
                Start Over
              </b-button>
            </div>

            <hr />

            <!-- Saved Instances -->
            <div v-if="instances.length > 0" class="saved-instances">
              <h6 class="text-muted small text-uppercase mb-3">
                <i class="fas fa-history mr-1"></i>
                Previous Instances ({{ instances.length }})
              </h6>
              <div class="instance-list">
                <div
                  v-for="instance in instances.slice(0, 3)"
                  :key="instance.id"
                  class="instance-item"
                >
                  <div class="instance-info">
                    <span class="instance-name">{{ instance.config?.name || 'Unnamed Dashboard' }}</span>
                    <span class="instance-date">{{ formatDate(instance.createdAt) }}</span>
                  </div>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="loadAndViewInstance(instance.id)"
                  >
                    <i class="fas fa-eye"></i>
                  </b-button>
                </div>
              </div>
            </div>
          </b-card>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="navigation-buttons mt-4 d-flex justify-content-between">
        <b-button variant="outline-secondary" @click="goBack">
          <i class="fas fa-arrow-left mr-2"></i>
          Back to Settings
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapState, mapActions, mapMutations, mapGetters,
} from 'vuex';

export default {
  name: 'PreviewConfiguration',

  data() {
    return {
      isCreatingDashboard: false,
    };
  },

  computed: {
    ...mapState('configurer', [
      'dashboardConfig',
      'appearanceConfig',
      'transformedData',
      'dataSummary',
      'columnMappingConfig',
      'instances',
      'activeInstanceId',
    ]),
    ...mapGetters('configurer', ['isConfigComplete', 'isDataReady']),

    config() {
      return this.dashboardConfig;
    },

    appearance() {
      return this.appearanceConfig;
    },

    columnMapping() {
      return this.columnMappingConfig;
    },

    hasData() {
      return this.transformedData && this.transformedData.length > 0;
    },

    previewHeaderStyle() {
      const primaryColor = this.appearance?.colors?.primary || '#007bff';
      return {
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${this.appearance?.colors?.secondary || '#6c757d'} 100%)`,
      };
    },
  },

  mounted() {
    this.fetchInstances();
  },

  methods: {
    ...mapActions('configurer', ['createInstance', 'loadInstance', 'fetchInstances', 'resetConfigurer']),
    ...mapMutations('configurer', ['DELETE_INSTANCE']),

    async createDashboard() {
      if (!this.isConfigComplete) {
        this.$bvToast.toast('Please complete all configuration steps first', {
          title: 'Configuration Incomplete',
          variant: 'warning',
          solid: true,
        });
        return;
      }

      this.isCreatingDashboard = true;

      try {
        const instance = await this.createInstance();

        this.$swal({
          icon: 'success',
          title: this.activeInstanceId ? 'Dashboard Updated!' : 'Dashboard Created!',
          text: this.activeInstanceId ? 'Your custom MSDAT dashboard has been updated successfully.' : 'Your custom MSDAT dashboard has been created successfully.',
          showCancelButton: true,
          confirmButtonText: 'View Dashboard',
          cancelButtonText: 'Stay Here',
        }).then((result) => {
          if (result.isConfirmed) {
            this.$router.push({
              name: 'generated-dashboard',
              params: { instanceId: instance.id },
            });
          }
        });
      } catch (error) {
        console.error('Error creating dashboard:', error);
        const isPermissionError = error?.code === 'permission-denied'
          || error?.message?.toLowerCase().includes('permission');
        this.$swal({
          icon: 'error',
          title: isPermissionError ? 'Firebase Permission Error' : 'Error',
          text: isPermissionError
            ? 'Your Firebase account is signed in, but Firestore rules are blocking dashboard_instances writes.'
            : error.message || 'Failed to create dashboard. Please try again.',
        });
      } finally {
        this.isCreatingDashboard = false;
      }
    },

    downloadConfig() {
      if (!this.config) {
        this.$bvToast.toast('No configuration to download', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
        return;
      }

      try {
        const exportData = {
          config: this.config,
          appearance: this.appearance,
          columnMapping: this.columnMapping,
          dataSummary: this.dataSummary,
          exportedAt: new Date().toISOString(),
        };

        const configStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([configStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(this.config.name || 'dashboard').toLowerCase().replace(/\s+/g, '-')}-config.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        this.$bvToast.toast('Configuration downloaded successfully', {
          title: 'Download Complete',
          variant: 'success',
          solid: true,
        });
      } catch (error) {
        console.error('Error downloading config:', error);
        this.$bvToast.toast('Failed to download configuration', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    },

    openFullPreview() {
      this.$router.push({
        name: 'generated-dashboard',
        params: { instanceId: 'preview' },
      });
    },

    async loadAndViewInstance(instanceId) {
      try {
        await this.loadInstance(instanceId);
        this.$router.push({
          name: 'generated-dashboard',
          params: { instanceId },
        });
      } catch (error) {
        console.error('Error loading instance:', error);
        this.$bvToast.toast('Failed to load dashboard instance', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    },

    resetAndStartOver() {
      this.$swal({
        icon: 'warning',
        title: 'Start Over?',
        text: 'This will clear all your current configuration. Are you sure?',
        showCancelButton: true,
        confirmButtonText: 'Yes, Start Over',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc3545',
      }).then((result) => {
        if (result.isConfirmed) {
          this.resetConfigurer();
          this.$router.push({ name: 'configure-customize' });
        }
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },

    goBack() {
      this.$router.push({ name: 'configure-settings' });
    },
  },
};
</script>

<style scoped>
.preview-container {
  min-height: 100vh;
  /* background-color: #f8f9fa; Removed */
  padding-bottom: 2rem;
}

.step-header h2 {
  color: #2c3e50;
  font-weight: 700;
}

/* Glass Cards */
.summary-card,
.preview-card,
.actions-card {
  border: none;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
  margin-bottom: 24px;
}

::v-deep .card-header {
  background-color: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  border-radius: 20px 20px 0 0 !important;
}

/* Info Rows & Stats */
.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.info-label {
  color: #636e72;
  font-weight: 600;
}

.info-value {
  color: #2c3e50;
  font-weight: 700;
}

.stat-item {
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.05);
}

/* Tags */
.tag-list .badge {
  font-weight: 500;
  padding: 0.5rem 0.8rem;
  border: 1px solid rgba(0,0,0,0.1);
  background: rgba(255,255,255,0.8);
  color: #2d3436;
}

/* Preview Mini */
.preview-mini {
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

.preview-body {
  padding: 1.5rem;
  background-color: #f8f9fa;
}

.preview-chart-placeholder {
  background-color: #fff;
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

/* Action Buttons */
.action-buttons .btn {
  margin-bottom: 1rem;
  border-radius: 12px;
  padding: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
}

.action-buttons .btn-primary {
    background: #007D53;
    border-color: #007D53;
    box-shadow: 0 4px 15px rgba(0, 125, 83, 0.3);
}

.action-buttons .btn-primary:hover {
    background: #006644;
    transform: translateY(-2px);
}

/* Instance List */
.instance-item {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  margin-bottom: 0.75rem;
  border: 1px solid rgba(0,0,0,0.05);
  transition: all 0.2s ease;
}

.instance-item:hover {
    background-color: rgba(255, 255, 255, 0.8);
}

/* Navigation */
.navigation-buttons {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background-color: rgba(255, 255, 255, 0.5);
  margin-top: 2rem;
  border-radius: 0 0 20px 20px;
  backdrop-filter: blur(10px);
}
</style>
